/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Flex,
  SimpleGrid,
} from "@chakra-ui/react";
import { useWindowWidth } from "@react-hook/window-size";

// Components
import ProjectDetailTable from "./projectDetailTable";
import ProjectDetailMobileTable from "./projectDetailMobileTable";
import MessageBox from "components/messagebox/MessageBox";
import { ProjectHeader } from "./projectDetail/components/ProjectHeader";
import { CustomerInfo } from "./projectDetail/components/CustomerInfo";
import { ProjectManagerInfo } from "./projectDetail/components/ProjectManagerInfo";
import { UnreadMessagesNotification } from "./projectDetail/components/UnreadMessagesNotification";
import { ProjectMetaInfo } from "./projectDetail/components/ProjectMetaInfo";
import { ProjectNotes } from "./projectDetail/components/ProjectNotes";
import { DownloadActions } from "./projectDetail/components/DownloadActions";
import { ProjectProgress } from "./projectDetail/components/ProjectProgress";
import { DocumentStatusControls } from "./projectDetail/components/DocumentStatusControls";

// Hooks
import { useProjectDetail } from "./projectDetail/hooks/useProjectDetail";
import { useTrackingDetails } from "./projectDetail/hooks/useTrackingDetails";
import { useTargetLanguages } from "./projectDetail/hooks/useTargetLanguages";

// Helpers
import { createProjectMeta, getCustomerInfo, getProjectManagers, getDocuments } from "./projectDetail/helpers/projectHelpers";

// Constants
import { DOCUMENT_STATUS } from "./projectDetail/helpers/constants";

export default function ProjectDetail(props: {
  selectedProjectId: any | null;
  onCloseModal: Function;
  onRefresh: Function;
}) {
  const { selectedProjectId, onCloseModal, onRefresh } = props;
  const windowWidth = useWindowWidth();
  const projectDetailRef = useRef();

  // State for document status controls
  const [isApplyAll, setIsApplyAll] = useState(false);
  const [allDocumentStatus, setAllDocumentStatus] = useState({
    id: 1,
    label: "New",
  });
  // Custom hooks
  const {
    selectedProject,
    isFetching,
    hasChanged,
    unreadMessageCnt,
    isUpdatingProjectStatus,
    onUpdateDocument,
    onDownloadFiles,
    onClickUpdateDocumentsStatus: originalOnClickUpdateDocumentsStatus,
    setHasChanged
  } = useProjectDetail(selectedProjectId);

  const { trackingDetails } = useTrackingDetails(selectedProjectId);
  
  const {
    selectedTargetLanguages,
    availableTargetLanguages,
    orderTargetLanguages,
  } = useTargetLanguages(selectedProject);

  // Computed values
  const projectMeta = useMemo(() => createProjectMeta(selectedProject), [selectedProject]);
  const customer = useMemo(() => getCustomerInfo(selectedProject), [selectedProject]);
  const projectManagers = useMemo(() => getProjectManagers(selectedProject), [selectedProject]);
  const documents = useMemo(() => 
    getDocuments(selectedProject, orderTargetLanguages, selectedTargetLanguages), 
    [orderTargetLanguages, selectedProject, selectedTargetLanguages]
  );

  // Reset hasChanged when project changes
  useEffect(() => {
    if (selectedProjectId) {
      setHasChanged(false);
    }
  }, [selectedProjectId, setHasChanged]);

  // Event handlers
  const onClickViewNotifications = () => {
    if (projectDetailRef.current) {
      const elem: any = projectDetailRef.current;
      elem.scrollTo({
        top: 10000,
        behavior: "smooth",
      });
    }
  };

  const handleUpdateDocumentsStatus = () => {
    originalOnClickUpdateDocumentsStatus(allDocumentStatus);
  };

  return (
    <Box
      w={{
        base: "100%",
        xl: "calc(100vw - 230px)",
        baseWidth: "calc(100vw - (100vw - 1440px) / 2 - 230px)",
      }}
      ref={projectDetailRef}
      transition="transform 0.3s ease-in-out"
      transform={selectedProjectId ? `translateX(0)` : `translateX(100%)`}
      h="100vh"
      position="fixed"
      bgColor="#FFF"
      zIndex="200"
      boxShadow={selectedProjectId ? "0px 4px 40px rgba(0, 0, 0, 0.1)" : "none"}
      boxSizing="border-box"
      padding={{
        md: "50px 40px 60px",
        base: "70px 20px 30px",
        baseWidth: "50px calc((100vw - 1440px) / 2 + 40px) 50px 60px",
      }}
      overflow="auto"
      top="0px"
      right="0px"
    >
      <Flex
        minH="100%"
        h="fit-content"
        overflow={"hidden"}
        pr="20px"
        mr="-20px"
      >
        <Flex flex="1" direction="column" width="0">
          <ProjectHeader
            selectedProject={selectedProject}
            isFetching={isFetching}
            hasChanged={hasChanged}
            onCloseModal={onCloseModal}
            onRefresh={onRefresh}
          />

          <SimpleGrid
            columns={{ base: 1, sm: 1, md: 2, xl: 2 }}
            gap="20px"
            marginTop="50px"
            marginBottom={{ md: "70px", base: "30px" }}
          >
            <Box>
              <Flex
                flexWrap="wrap"
                justifyContent="space-between"
                mb="25px"
              >
                <CustomerInfo customer={customer} />
                <ProjectManagerInfo projectManagers={projectManagers} />
                <UnreadMessagesNotification 
                  unreadMessageCnt={unreadMessageCnt}
                  onClickViewNotifications={onClickViewNotifications}
                />
              </Flex>

              <ProjectMetaInfo 
                projectMeta={projectMeta}
                isFetching={isFetching}
              />

              <ProjectNotes selectedProject={selectedProject} />

              <DownloadActions 
                onDownloadFiles={onDownloadFiles}
                trackingDetails={trackingDetails}
              />
            </Box>

            <ProjectProgress documents={documents} />
          </SimpleGrid>

          <DocumentStatusControls
            isApplyAll={isApplyAll}
            setIsApplyAll={setIsApplyAll}
            allDocumentStatus={allDocumentStatus}
            setAllDocumentStatus={setAllDocumentStatus}
            documentStatus={DOCUMENT_STATUS}
            isUpdatingProjectStatus={isUpdatingProjectStatus}
            onClickUpdateDocumentsStatus={handleUpdateDocumentsStatus}
          />

          <Box position="relative">
            {windowWidth >= 768 ? (
              <ProjectDetailTable
                isFetching={isFetching}
                tableData={documents}
                onUpdateDocument={onUpdateDocument}
                availableTargetLanguages={availableTargetLanguages}
              />
            ) : (
              <ProjectDetailMobileTable
                isFetching={isFetching}
                tableData={documents}
                onUpdateDocument={onUpdateDocument}
                availableTargetLanguages={availableTargetLanguages}
              />
            )}
          </Box>

          <MessageBox projectId={selectedProjectId} />
        </Flex>
      </Flex>
    </Box>
  );
}
